// Import necessary functions and types from utilities and services
import { functions, httpsCallable } from '@util/firebase';
import { logError } from '@util/logError';
import { logVendorError } from '@util/logVendorError';
import { OrderDocument } from '@util/types/firestore/order';
import { markAsDelivered } from '../orders/orders.service';
import { AddressDocument } from '@util/types/firestore/address';
import {
  getEstimatesArgs,
  Rate,
  TrackPackageResponse,
} from './shipengine.types';
import {
  selectVerificationEvent,
  verifyDeliveryLocation,
} from './deliveryLocationMatch';

// Call the cloud function 'getEstimates' with the provided arguments
export async function getEstimates(args: getEstimatesArgs): Promise<Rate[]> {
  try {
    const { data } = await httpsCallable<getEstimatesArgs, Rate[]>(
      functions,
      'getEstimates'
    )(args);
    return data;
  } catch (error) {
    logError(error);
    return [];
  }
}


// The 'getEstimates' Firebase Cloud Function calculates estimated shipping rates
// for products using the ShipEngine API. It requires a product ID, destination
// country code, and postal code, fetches product details from Firestore, and
// filters the API response to return relevant shipping rates with a 20% markup.
// The function manages error cases like missing fields and logs any API
// communication errors, ensuring users receive accurate shipping cost estimates.

// Call the cloud function 'trackPackage' with the carrier and tracking number
export async function trackPackage(
  carrier: string,
  trackingNo: string
): Promise<TrackPackageResponse> {
  const { data } = await httpsCallable<
    { carrier: string; trackingNo: string },
    TrackPackageResponse
  >(
    functions,
    'trackPackage'
  )({ carrier, trackingNo });
  return data;
}

// getRates (Cloud Function) was removed in the checkout migration (api commit
// c13c138). Shipping rates are fetched via getProductRates -> the Next.js API
// route /api/checkout/shipping-rates. GB #1313.

// Fetch shipping rates for a single product via our Next.js API route.
// Checkout passes the product's shipment; the offer/bid modal omits it (the PDP
// strips shipment for seller-address privacy) and the route fetches it server-side.
export async function getProductRates(
  product: { product_id: string; price: number; categories?: any[]; shipment: any },
  ship_to: AddressDocument
): Promise<Rate[]> {
  const res = await fetch('/api/checkout/shipping-rates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ products: [product], ship_to }),
  });

  if (!res.ok) {
    // GB #1527: `err.error` is the only thing that reaches Sentry, and for the
    // route's validation failure it is the constant string 'Invalid request'.
    // The `details` payload naming the fields at fault was dropped on the
    // floor. Carry the whole body on the error instead.
    //
    // Read the body ONCE, as text. It used to be `res.json().catch(() => ({}))`
    // — a single-consumption read that THROWS on a non-JSON body, with the catch
    // swallowing it and substituting an empty object. So the 502 / 504 / HTML
    // error page, the one case where the response body IS the entire diagnosis,
    // reached Sentry as `body: "{}"` and the diagnostic was silently empty.
    // Parsing from the text keeps both shapes: structured when the body is JSON,
    // the raw upstream text when it is not. A raw string flows through
    // logVendorError's string branch, which scrubs at FULL length and only then
    // caps at 2KB, so a large HTML page is redacted BEFORE it is truncated
    // rather than after.
    const bodyText = await res.text().catch(() => '');
    let parsedBody: unknown;
    try {
      parsedBody = bodyText ? JSON.parse(bodyText) : undefined;
    } catch {
      parsedBody = undefined;
    }

    // The message becomes a Sentry issue TITLE, which no redactor reads. It is
    // therefore ALWAYS a constant plus the status — never a value from the
    // response, not even a short plain one.
    //
    // A length-and-type gate was not enough. A platform, proxy, or WAF can
    // answer a 502/504 with its own JSON, and `{"error":"Failed to fetch
    // upstream"}` is both short and a plain string — so it would have been
    // accepted as the title, and Sentry's `ignoreErrors` matches 'Failed to
    // fetch' by SUBSTRING and would have dropped the event before beforeSend
    // ever ran. That is precisely the silent-drop bug this change exists to fix,
    // reintroduced through the back door by an untrusted upstream. Shorter
    // strings could equally put arbitrary prose or sensitive text in the title.
    //
    // Nothing is lost. The response body — including its `error` field — is
    // attached below and goes through logVendorError's scrub-then-cap path, so
    // the diagnosis is still in the event, where a redactor can reach it.
    throw logVendorError(
      new Error(`Shipping rate request rejected (HTTP ${res.status})`),
      'shipengine-rates',
      {
        status: res.status,
        statusText: res.statusText,
        body: parsedBody !== undefined ? parsedBody : bodyText,
      }
    );
  }

  const data = await res.json();
  const result = data.results?.[0];
  if (result?.error) {
    // 200 with a per-product error. This string becomes the thrown Error's
    // message, which Sentry uses as the exception value and the ISSUE TITLE —
    // and logVendorError redacts only the attached `body`, never a message. So
    // the route is responsible for keeping request-derived text out of `error`,
    // and it does: every string it can put there is a fixed constant, optionally
    // suffixed with a validated ShipEngine `error_code` enum. It used to forward
    // the carrier's free-text `message`, which the carrier can build from the
    // buyer's address. See the rate_response.errors branch in
    // src/pages/api/checkout/shipping-rates.ts.
    //
    // That invariant is now TOTAL: every branch of the route — missing shipment,
    // carrier rejection, timeout, transport failure — emits a constant, at most
    // suffixed by one shape-validated identifier (a ShipEngine error_code enum or
    // a thrown error's class name). No branch forwards a vendor or runtime
    // message. See sanitizeDiagnosticToken in that file.
    //
    // `status` here is the TRANSPORT status, which is 200 — the request
    // succeeded and the failure is in the payload. Recording only that read as
    // success in Sentry, so the outcome says plainly which kind of failure this
    // is; a transport failure above leaves `outcome` unset.
    throw logVendorError(new Error(result.error), 'shipengine-rates', {
      status: res.status,
      outcome: 'per_product_error',
      body: {
        product_id: result.product_id,
        rates_returned: result.rates?.length ?? 0,
        // The diagnosis the carrier's prose used to carry, in a form that has no
        // address in it. Absent on the branches that have no carrier error.
        ...(result.error_code ? { carrier_error_code: result.error_code } : {}),
        ...(result.error_count
          ? { carrier_error_count: result.error_count }
          : {}),
      },
    });
  }
  return result?.rates ?? [];
}

// Call the cloud function 'fixLabel' with the order ID
export async function fixLabels(orderId: string) {
  const { data } = await httpsCallable<
    { orderId: string },
    { updated: number; errors: string[] }
  >(
    functions,
    'fixLabel'
  )({ orderId });
  return data;
}

// Call the cloud function 'voidLabel' with the label ID
export async function voidLabel(label_id: string) {
  const { data } = await httpsCallable<
    { label_id: string; tn?: string }, // if label_id is unavailable, use tracking_number
    { approved: boolean; message: string }
  >(
    functions,
    'voidLabel'
  )({ label_id });
  return data;
}

// Function to get the cheapest shipping estimate
export async function getCheapestEstimate(
  product_id: string,
  to_postal_code: string,
  to_country_code = 'US'
) {
  // Get rates for the provided arguments
  const rates = await getEstimates({
    product_id,
    to_postal_code,
    to_country_code,
  });

  // Determine if any of the rates are for international shipping
  const isInternational = rates.some((r) =>
    r.service_code?.includes('international')
  );

  // Define service codes based on whether the shipping is international or not
  const service_codes = isInternational
    ? [
        'usps_priority_mail_international',
        'ups_standard_international',
        'fedex_international_economy',
      ]
    : ['usps_priority_mail', 'ups_ground', 'fedex_home_delivery'];

  // Find the cheapest rate among the defined service codes
  let cheapest = 0;
  rates.forEach((rate) => {
    if (service_codes.includes(rate.service_code))
      cheapest = cheapest
        ? Math.min(cheapest, rate.total_amount)
        : rate.total_amount;
  });

  // Return the cheapest rate found
  return cheapest;
}

// Function to check if a package has been delivered
export const checkDelivery = async (
  trackingResponse: TrackPackageResponse,
  order: OrderDocument,
  itemState: number,
  isAdmin: boolean
) => {
  // Return early if no tracking response or sellers
  if (!trackingResponse || !order.sellers) return;

  try {
    // Check if the package has been delivered and the item state is
    if (
      trackingResponse.status_code === 'DE' && // DE = Delivered
      itemState === 1 &&
      trackingResponse.tracking_number
    ) {
      // api#396: postal when the carrier gives one, city+state fallback ONLY
      // for FedEx (Bill's ruling 2026-08-26). selectVerificationEvent picks the
      // newest located event, not a blank rescan at index 0. The carrier is
      // read from the order item that carries this tracking number.
      const trackingNo = trackingResponse.tracking_number.toUpperCase();
      const matchedCarrier = Object.values(order.sellers ?? {})
        .flat()
        .find(
          (item) => item.tracking_number?.toUpperCase() === trackingNo
        )?.carrier;
      const verificationEvent = selectVerificationEvent(
        trackingResponse.events
      );
      const verdict = verifyDeliveryLocation(
        order.address,
        verificationEvent,
        matchedCarrier
      );

      // Advance only when the delivery location verifies against the ship-to.
      if (verdict.verified) {
        let foundTracking = false;

        // Iterate over sellers' items to find the matching tracking number
        Object.keys(order.sellers ?? {}).find((sellerId) => {
          order.sellers?.[sellerId]?.forEach((item) => {
            if (item.tracking_number)
              item.tracking_number = item.tracking_number.toUpperCase();
            if (
              item.tracking_number ===
              trackingResponse.tracking_number.toUpperCase()
            ) {
              const milliToHour = 2.7777777777778e-7;
              const delivery_time =
                new Date(
                  trackingResponse.actual_delivery_date || 0
                ).valueOf() || Date.now();

              // Check if delivery time is at least 24 hours after order creation  
              if ((delivery_time - order.created) * milliToHour > 24) {
                item.state = 2; // Update item state to delivered
                item.delivery_time = delivery_time;
                foundTracking = true;
              } else {
                if (isAdmin)
                  alert(
                    `Delivery Time (${delivery_time}) isn't 24hrs after Order Creation (${order.created})`
                  );
                return;
              }
            }
          });
          return foundTracking;
        });

        // If the tracking number is found and the admin is signed in, update the order status
        if (foundTracking) {
          if (isAdmin) {
            await markAsDelivered(order);
            alert('Order status updated to delivered');
          }
        }
      } else if (isAdmin) {
        const orderLoc = [
          order.address?.city_locality,
          order.address?.state_province,
          order.address?.postal_code,
        ]
          .filter(Boolean)
          .join(' ');
        const trackLoc = [
          verificationEvent?.city_locality,
          verificationEvent?.state_province,
          verificationEvent?.postal_code,
        ]
          .filter(Boolean)
          .join(' ');
        alert(
          verdict.reason === 'location_unverifiable'
            ? `Carrier returned no comparable delivery location. Order ship-to: ${
                orderLoc || '(none)'
              }. Verify manually before marking delivered.`
            : `Delivery location doesn't match. Order ship-to: ${
                orderLoc || '(none)'
              } — Tracking shows: ${trackLoc || '(none)'}.`
        );
      }
    }
  } catch (e) {
    logError(e);
    if (isAdmin) alert('Error updating order, check console');
  }
};
